// THE shared financial source of truth (Round 14 unification, fixes audit C1
// + H4 + L2). Every surface that shows a money total — the Overview net, the
// Summary ledger subtotals, costs-by-context, and the AI prompt block — is a
// presentation of buildLedger()'s output, NOT a parallel computation. They
// therefore agree by construction.
//
// Pipeline (all deterministic, no AI):
//   1. computeCaseTotals = within-record dedup + fold-subsumption + exclude +
//      bucket classification (reused as a primitive, unchanged).
//   2. cross-record merge of money candidates on (amount, party, day) — so a
//      figure restated across records is ONE transaction, not double-counted.
//   3. effective disposition per transaction (status > bucket-choice > AI
//      default); untriaged ⇒ suppresses the net (verify-or-suppress).
//   4. amount_cents_override applied to the transaction amount.
//   5. net / subtotals via sumCents ONLY.

import type { RecordFact } from "../types";
import {
  computeCaseTotals,
  bucketSubtotalCents,
  sumCents,
  type CategoryOverrides,
  type ExcludedAmount,
  type LedgerBucket,
  type LedgerItem,
} from "./financialTotals";

// Tracker status vocabulary. null = not yet triaged. Lives here (the shared
// layer) and is re-exported by reimbursementSummary for existing importers.
export type TxnStatus =
  | "Received"
  | "Paid"
  | "Estimated"
  | "Quote"
  | "Need receipt"
  | "Disputed"
  | "Outstanding"
  | "Accruing"
  | "Documented — uncosted"
  | "Waived / no cost";

export const TXN_STATUSES: TxnStatus[] = [
  "Received",
  "Paid",
  "Estimated",
  "Quote",
  "Need receipt",
  "Disputed",
  "Outstanding",
  "Accruing",
  "Documented — uncosted",
  "Waived / no cost",
];

// Single source of truth for what each status MEANS and what it DOES (where it
// routes / whether it counts). The dropdown options, the row's status control
// tooltip, and the tests all read from here — never duplicate these strings.
// `definition` = what it means; `routing` = what it does. statusTooltip() joins
// them for display. The null/untriaged pseudo-status is UNTRIAGED_DEF.
export interface StatusDef {
  label: string; // dropdown / control text (equals the enum value for real statuses)
  definition: string; // what it means
  routing: string; // what it does — where it routes / whether it counts
}

export const STATUS_DEFS: Record<TxnStatus, StatusDef> = {
  Paid: { label: "Paid", definition: "money you actually paid out", routing: "counts in Out-of-pocket" },
  Received: { label: "Received", definition: "money that actually came to you", routing: "counts in Payments received" },
  Estimated: { label: "Estimated", definition: "a projected figure, not a real transaction", routing: "not counted" },
  Quote: { label: "Quote", definition: "a price quoted/offered, not paid", routing: "not counted" },
  "Need receipt": { label: "Need receipt", definition: "a real cost you owe, awaiting documentation", routing: "moves to TBD, not counted until paid" },
  Outstanding: { label: "Outstanding", definition: "unresolved; amount knowable but not yet captured", routing: "moves to TBD, not counted until paid" },
  Accruing: { label: "Accruing", definition: "still actively running up (rental, daily storage); final amount not yet known", routing: "moves to TBD, not counted until final" },
  "Documented — uncosted": { label: "Documented — uncosted", definition: "a confirmed, documented item with no price yet — deliberately absent from the estimate (not a running meter)", routing: "excluded from the current total — flags that the headline figure is understated" },
  Disputed: { label: "Disputed", definition: "contested", routing: "shown but not counted" },
  "Waived / no cost": { label: "Waived / no cost", definition: "a real charge that resolved to $0", routing: "shown for the narrative, not counted" },
};

// The empty (null) selection — not a TxnStatus, so it lives outside the map.
export const UNTRIAGED_DEF: StatusDef = {
  label: "Untriaged (needs status)",
  definition: "not yet categorized",
  routing: "suppresses the net until set",
};

// Tooltip text for a status (null = untriaged). One place, so every surface
// reads the same string.
export function statusTooltip(status: TxnStatus | null): string {
  const d = status ? STATUS_DEFS[status] : UNTRIAGED_DEF;
  return `${d.definition} — ${d.routing}.`;
}

export interface StatusOverride {
  status: TxnStatus | null;
  amountCentsOverride: number | null;
}

export interface LedgerOverrides {
  bucketOverrides: CategoryOverrides; // user bucket choices (cost/recovery/coverage_term/rate/exclude)
  statusOverrides: ReadonlyMap<string, StatusOverride>; // status + amount, content-keyed
  mergeLinks: ReadonlyMap<string, "merge" | "separate">;
}

// A money transaction: one or more verified amount facts (cross-record merged)
// that represent the same real charge/receipt.
export interface LedgerTransaction {
  key: string; // representative content key (stable across re-extraction)
  items: LedgerItem[]; // merged source items
  party: string | null;
  dateRaw: string | null;
  rawAmountCents: number; // representative parsed amount (pre-override)
  amountCents: number; // effective amount (override applied)
  amountEdited: boolean;
  bucket: LedgerBucket; // "cost" | "recovery" (only money buckets become txns)
  status: TxnStatus | null; // effective disposition; null = untriaged
  possiblySameAsKey: string | null; // ambiguous duplicate partner awaiting decision
  mergedCount: number;
}

export interface LedgerNet {
  costCents: number; // Σ Paid transactions
  recoveryCents: number; // Σ Received transactions
  netCents: number; // cost − recovery
  // Asserted only when every money transaction is triaged (verify-or-suppress).
  asserted: boolean;
  reason: string | null; // why not asserted (for the UI notice)
  // cost + recovery (from transactions) AND coverage_term + rate (reference,
  // never in net) — drop-in for the old totals.bucketSubtotals consumers.
  bucketSubtotals: Array<{ bucket: LedgerBucket; cents: number }>;
}

export interface LedgerResult {
  transactions: LedgerTransaction[];
  net: LedgerNet;
  // Representative items of untriaged transactions — the confirm worklist.
  toConfirm: LedgerItem[];
  // Passthrough from computeCaseTotals: unverified/unparseable amounts.
  excluded: ExcludedAmount[];
  // Passthrough deduped items (for "excluded from total" displays / hasAmounts).
  items: LedgerItem[];
}

// ---- disposition heuristic (deterministic, no AI) -------------------------

const ESTIMATE_RE =
  /\b(estimat|quote|approx|gross|cap\b|maximum|coverage limit|per day|\/day|per hour|\/hr|rate)\b/i;
const PAID_RE = /\b(paid|charged|out[- ]of[- ]pocket|i paid|we paid)\b/i;
const RECEIVED_RE =
  /\b(received|issued|net|reimburs|deposited|credited|payment of)\b/i;

// AI-default disposition from the merged facts' flow + language. Conservative:
// only unambiguous "money actually moved" auto-enters the net; estimate/offer/
// rate-flavoured is Estimated (excluded); everything else stays untriaged.
export function deriveStatus(items: LedgerItem[]): TxnStatus | null {
  const text = items
    .map((i) => `${i.fact.summary} ${i.fact.verbatim_quote}`)
    .join(" ");
  const flows = new Set(items.map((i) => i.fact.amount_flow ?? ""));
  const buckets = new Set(items.map((i) => i.bucket));
  if (
    flows.has("offer") ||
    flows.has("rate") ||
    buckets.has("rate") ||
    buckets.has("coverage_term")
  ) {
    return "Estimated";
  }
  if (ESTIMATE_RE.test(text) && !PAID_RE.test(text) && !RECEIVED_RE.test(text)) {
    return "Estimated";
  }
  if (buckets.has("recovery") && RECEIVED_RE.test(text) && !/\bgross\b/i.test(text)) {
    return "Received";
  }
  if (buckets.has("cost") && PAID_RE.test(text)) {
    return "Paid";
  }
  return null; // untriaged
}

// ---- helpers --------------------------------------------------------------

export function normParty(p: string | null): string {
  return (p ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}
export function pairKey(a: string, b: string): string {
  return a < b ? `${a}::${b}` : `${b}::${a}`;
}

// ---- description similarity (Round 16: grouping-first) --------------------
// Deterministic token-set Jaccard over a charge's DESCRIPTION. Amount tokens
// and generic finance/STAGE words are stripped: "$450 fee" and "$450 cost"
// must not read as similar on a generic word alone, and "estimate"/"actual" of
// the same charge must not read as DIFFERENT (stage is the cross-stage signal,
// handled separately). Light stemming + hyphen-join so "re-key" ≈ "re-keying".
const GENERIC_DESC_WORDS = new Set([
  // generic finance nouns (don't distinguish one charge from another)
  "cost", "fee", "charge", "payment", "paid", "pay", "total", "amount",
  "invoice", "bill", "balance", "due", "price", "sum", "subtotal", "expense",
  "reimbursement", "reimburse", "receipt", "usd", "dollar", "money",
  // stage words — estimate vs actual is the cross-stage signal, NOT a
  // description difference, so they must not push descriptions apart.
  "estimate", "actual", "quote", "approx", "approximate", "gross", "net",
  "current", "final", "initial", "projected", "revised",
  // stopwords
  "for", "the", "and", "of", "to", "in", "on", "at", "by", "with", "from",
  "my", "our", "your", "this", "that", "it", "is", "was", "were", "be",
  "been", "as", "or", "per", "has", "have", "had", "will", "shall", "are",
  "a", "an", "re", "no", "not", "all", "any", "its",
  // month names — dates leak into summaries and add spurious overlap/noise.
  "january", "february", "march", "april", "may", "june", "july", "august",
  "september", "october", "november", "december",
]);

function stemToken(t: string): string {
  let s = t;
  // plural → singular (do NOT turn "services" into "servic": strip just "s")
  if (s.endsWith("ies") && s.length > 4) s = s.slice(0, -3) + "y";
  else if (/(ses|xes|zes|ches|shes)$/.test(s)) s = s.slice(0, -2);
  else if (s.endsWith("s") && !s.endsWith("ss") && s.length > 3) s = s.slice(0, -1);
  // verb endings
  if (s.endsWith("ing") && s.length > 5) s = s.slice(0, -3);
  else if (s.endsWith("ed") && s.length > 4) s = s.slice(0, -2);
  // undouble a trailing consonant left by suffix stripping: reprogramm→reprogram
  if (/([bcdfgklmnprt])\1$/.test(s)) s = s.slice(0, -1);
  return s;
}

export function descTokens(text: string | null): Set<string> {
  const cleaned = (text ?? "")
    .toLowerCase()
    .replace(/[-']/g, "") // join hyphenated: re-key → rekey
    .replace(/[^a-z]+/g, " "); // drop digits, currency, punctuation
  const out = new Set<string>();
  for (const raw of cleaned.split(/\s+/)) {
    if (!raw) continue;
    const tok = stemToken(raw);
    if (tok.length < 3) continue; // drop fragments
    if (GENERIC_DESC_WORDS.has(tok) || GENERIC_DESC_WORDS.has(raw)) continue;
    out.add(tok);
  }
  return out;
}

// Jaccard of the two descriptions' content tokens. Empty-vs-empty (both
// descriptions are all-generic, no distinguishing content) returns a MIDDLE
// value so such pairs are surfaced as "possibly same" rather than silently
// auto-grouped or hard-split; empty-vs-nonempty returns 0 (cannot confirm).
export function descSimilar(a: string | null, b: string | null): number {
  const A = descTokens(a);
  const B = descTokens(b);
  if (A.size === 0 && B.size === 0) return 0.4; // ambiguous → middle band
  if (A.size === 0 || B.size === 0) return 0;
  let inter = 0;
  for (const t of A) if (B.has(t)) inter++;
  return inter / (A.size + B.size - inter);
}

// Grouping thresholds, fitted against the golden suite + the Q50 case (see the
// grouping cases in reimbursementSummary.test.ts). >= AUTO auto-groups same
// amount+party; the [LOW, AUTO) middle band surfaces as "possibly same"; below
// LOW (genuinely different descriptions) stays separate, no prompt.
export const DESC_SIM_AUTO = 0.5;
export const DESC_SIM_LOW = 0.2;

// Minimal union-find for the grouping pass.
class DSU {
  private parent: number[];
  constructor(n: number) {
    this.parent = Array.from({ length: n }, (_, i) => i);
  }
  find(x: number): number {
    while (this.parent[x] !== x) {
      this.parent[x] = this.parent[this.parent[x]!]!;
      x = this.parent[x]!;
    }
    return x;
  }
  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent[Math.max(ra, rb)] = Math.min(ra, rb);
  }
}

interface Group {
  key: string;
  items: LedgerItem[];
  party: string | null;
  dateRaw: string | null;
  rawAmountCents: number;
  bucket: LedgerBucket;
}

interface Group {
  key: string;
  items: LedgerItem[];
  party: string | null;
  dateRaw: string | null;
  rawAmountCents: number;
  bucket: LedgerBucket;
}

function groupToTxn(items: LedgerItem[]): Group {
  const rep = items
    .slice()
    .sort((a, b) =>
      a.fact.created_at < b.fact.created_at
        ? -1
        : a.fact.created_at > b.fact.created_at
          ? 1
          : 0,
    )[0]!;
  return {
    key: rep.key,
    items,
    party: rep.fact.party,
    dateRaw: rep.fact.date,
    rawAmountCents: rep.cents,
    bucket: rep.bucket,
  };
}

// GROUPING-FIRST (Round 16): group mentions of the SAME real-world charge into
// one transaction by default, deterministically. Key = amount + normParty +
// description-similarity (NO day — a fact's date is the doc/email/forward/
// inferred date and wrongly splits same-charge mentions). The user only adjusts.
//
//   - auto-edge: same cents + same normParty + descSimilar >= AUTO.
//   - must-link: a user "merge" decision unions two items (cross-amount allowed
//     — the $199.99 estimate ↔ $450 actual case; the group then counts ONE
//     chosen amount, superseded mentions shown but excluded).
//   - cannot-link: a user "separate" decision removes the auto-edge for that
//     pair. EXCLUDE-a-mention writes "separate" between the mention and every
//     current sibling, so the mention is cut from all of them and falls out as
//     its own row (never silently dropped — it stays in the ledger to triage).
//
// Components are disjoint (union-find) ⇒ a partition: every counted item is in
// exactly one transaction, so no double-count and the no-drift invariant holds.
function groupCandidates(
  items: LedgerItem[],
  mergeLinks: ReadonlyMap<string, "merge" | "separate">,
): Group[] {
  const dsu = new DSU(items.length);
  const idxByKey = new Map<string, number>();
  items.forEach((it, i) => idxByKey.set(it.key, i));
  const isSeparate = (a: string, b: string) =>
    mergeLinks.get(pairKey(a, b)) === "separate";

  // Auto-edges, within (amount, party) buckets for efficiency.
  const buckets = new Map<string, number[]>();
  items.forEach((it, i) => {
    const k = `${it.cents}|${normParty(it.fact.party)}`;
    const arr = buckets.get(k);
    if (arr) arr.push(i);
    else buckets.set(k, [i]);
  });
  for (const idxs of buckets.values()) {
    for (let x = 0; x < idxs.length; x++) {
      for (let y = x + 1; y < idxs.length; y++) {
        const a = items[idxs[x]!]!;
        const b = items[idxs[y]!]!;
        if (isSeparate(a.key, b.key)) continue; // cannot-link
        if (descSimilar(a.fact.summary, b.fact.summary) >= DESC_SIM_AUTO) {
          dsu.union(idxs[x]!, idxs[y]!);
        }
      }
    }
  }

  // Must-links (explicit user merges), cross-amount allowed. Stale links whose
  // key no longer matches a current item are skipped (surfaced separately).
  for (const [pk, decision] of mergeLinks) {
    if (decision !== "merge") continue;
    const [ka, kb] = pk.split("::");
    const ia = idxByKey.get(ka!);
    const ib = idxByKey.get(kb!);
    if (ia === undefined || ib === undefined) continue;
    dsu.union(ia, ib);
  }

  const comp = new Map<number, LedgerItem[]>();
  items.forEach((it, i) => {
    const r = dsu.find(i);
    const arr = comp.get(r);
    if (arr) arr.push(it);
    else comp.set(r, [it]);
  });
  return [...comp.values()].map(groupToTxn);
}

// Possibly-same: distinct groups of the SAME amount the user might want to
// group. Cross-party (e.g. one $5,751.80 estimate restated by 4 parties) is
// always offered; same-party with a MIDDLE-band description (ambiguous, not
// confident enough to auto-group) is offered. Same-party with genuinely
// different descriptions (< LOW) is NOT offered — it stays cleanly separate.
// Cross-AMOUNT same-charge (estimate↔actual) is deliberately NOT proposed here
// — that's the Phase-2 AI suggester. A pair the user already decided is skipped.
function detectPossiblySame(
  groups: Group[],
  mergeLinks: ReadonlyMap<string, "merge" | "separate">,
): Map<string, string> {
  const out = new Map<string, string>();
  const byAmount = new Map<number, Group[]>();
  for (const g of groups) {
    const arr = byAmount.get(g.rawAmountCents);
    if (arr) arr.push(g);
    else byAmount.set(g.rawAmountCents, [g]);
  }
  for (const arr of byAmount.values()) {
    if (arr.length < 2) continue;
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        const a = arr[i]!;
        const b = arr[j]!;
        const pk = pairKey(a.key, b.key);
        if (mergeLinks.has(pk)) continue; // already merged/kept-separate
        const sameParty = normParty(a.party) === normParty(b.party);
        // Best match across the two groups' mentions, not just the reps — a
        // group's representative is the earliest mention and may word the
        // charge differently than the mention that actually matches.
        let sim = 0;
        for (const ia of a.items)
          for (const ib of b.items)
            sim = Math.max(sim, descSimilar(ia.fact.summary, ib.fact.summary));
        const qualifies = sameParty
          ? sim >= DESC_SIM_LOW && sim < DESC_SIM_AUTO // ambiguous middle
          : true; // cross-party, same amount → always offer
        if (!qualifies) continue;
        if (!out.has(a.key)) out.set(a.key, b.key);
        if (!out.has(b.key)) out.set(b.key, a.key);
      }
    }
  }
  return out;
}

// Effective disposition precedence — computed ONCE here so the net and the
// Summary agree, and so a status-only override can NEVER masquerade as a
// bucket confirmation (audit H4): an explicit status wins outright; the
// server-synthesized bucket on that row is simply never reached.
function effectiveStatus(
  g: Group,
  statusOverrides: ReadonlyMap<string, StatusOverride>,
  bucketOverrides: CategoryOverrides,
): TxnStatus | null {
  const ov = statusOverrides.get(g.key);
  if (ov?.status != null) return ov.status; // 1) explicit status
  // 2) a genuine Overview bucket choice (no status) → its money side.
  //    (A status-only row also carries a derived bucket, but step 1 caught it.)
  if (ov?.status == null && bucketOverrides.has(g.key)) {
    return g.bucket === "recovery" ? "Received" : "Paid";
  }
  return deriveStatus(g.items); // 3) AI default (null ⇒ untriaged)
}

// ---- main -----------------------------------------------------------------

export function buildLedger(
  facts: RecordFact[],
  overrides: LedgerOverrides,
): LedgerResult {
  const { bucketOverrides, statusOverrides, mergeLinks } = overrides;

  // 1) Within-record deduped, fold-subsumed, bucket-classified items.
  const totals = computeCaseTotals(facts, bucketOverrides);

  // Reference subtotals (coverage_term / rate) — never money in/out; kept
  // for the Overview's Costs-vs-coverage comparison. Within-record as before.
  const coverageRef = bucketSubtotalCents(totals.items, "coverage_term");
  const rateRef = bucketSubtotalCents(totals.items, "rate");

  // 2) Money candidates → GROUPING-FIRST: group mentions of the same charge by
  //    amount + party + description-similarity (no day), honoring user
  //    merge (must-link) / separate (cannot-link) decisions.
  const candidates = totals.items.filter(
    (i) => !i.excluded && !i.subsumed && (i.bucket === "cost" || i.bucket === "recovery"),
  );
  const groups = groupCandidates(candidates, mergeLinks);
  const possiblySame = detectPossiblySame(groups, mergeLinks);

  // 3 + 4) Disposition + amount override per transaction.
  const transactions: LedgerTransaction[] = groups.map((g) => {
    const ov = statusOverrides.get(g.key);
    const amountEdited = ov?.amountCentsOverride != null;
    const amountCents = amountEdited ? ov!.amountCentsOverride! : g.rawAmountCents;
    return {
      key: g.key,
      items: g.items,
      party: g.party,
      dateRaw: g.dateRaw,
      rawAmountCents: g.rawAmountCents,
      amountCents,
      amountEdited,
      bucket: g.bucket,
      status: effectiveStatus(g, statusOverrides, bucketOverrides),
      possiblySameAsKey: possiblySame.get(g.key) ?? null,
      mergedCount: g.items.length,
    };
  });

  // 5) Net via sumCents ONLY. Side is driven by STATUS (Received→recovery,
  //    Paid→cost), so a user can't be double-billed by a stale bucket.
  const paid = transactions.filter((t) => t.status === "Paid");
  const received = transactions.filter((t) => t.status === "Received");
  const costCents = sumCents(paid.map((t) => t.amountCents));
  const recoveryCents = sumCents(received.map((t) => t.amountCents));

  // Verify-or-suppress: an untriaged money transaction suppresses the net.
  const untriaged = transactions.filter((t) => t.status === null);
  const asserted = transactions.length > 0 && untriaged.length === 0;

  const bucketSubtotals: Array<{ bucket: LedgerBucket; cents: number }> = [];
  if (costCents !== 0 || paid.length > 0) bucketSubtotals.push({ bucket: "cost", cents: costCents });
  if (recoveryCents !== 0 || received.length > 0) bucketSubtotals.push({ bucket: "recovery", cents: recoveryCents });
  if (coverageRef !== 0) bucketSubtotals.push({ bucket: "coverage_term", cents: coverageRef });
  if (rateRef !== 0) bucketSubtotals.push({ bucket: "rate", cents: rateRef });

  const net: LedgerNet = {
    costCents,
    recoveryCents,
    netCents: costCents - recoveryCents,
    asserted,
    reason: asserted
      ? null
      : transactions.length === 0
        ? "No verified money transactions to total."
        : `${untriaged.length} amount${untriaged.length === 1 ? "" : "s"} need a status — triage to compute case totals.`,
    bucketSubtotals,
  };

  // The worklist: representative item of each untriaged transaction.
  const toConfirm = untriaged.map((t) => t.items[0]!);

  return { transactions, net, toConfirm, excluded: totals.excluded, items: totals.items };
}
